import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Package, Plus, Pencil, Trash2, Loader2, Search, ImageIcon, Upload, X } from "lucide-react";
import axios from "axios";

const API = "/api";
const BACKEND_URL = "";

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    image: "",
    images: [],
    price: "",
    agency_price: "",
    avg_delivery_time: "",
    in_stock: true,
    is_enabled: true,
    allow_quick_book: false,
    is_other_product: false,
    category_id: ""
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("ecomdemo_token")}` }
  });

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, getAuthHeaders());
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, getAuthHeaders());
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories");
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = async (e) => {
    const availableSlots = 4 - formData.images.length;
    const files = Array.from(e.target.files || []).slice(0, availableSlots);
    if (!files.length) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (files.some((file) => !allowedTypes.includes(file.type))) {
      toast.error("Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed.");
      return;
    }

    setUploading(true);
    try {
      const responses = await Promise.all(files.map((file) => {
        const uploadData = new FormData();
        uploadData.append("file", file);
        return axios.post(`${API}/upload`, uploadData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("ecomdemo_token")}`,
            "Content-Type": "multipart/form-data"
          }
        });
      }));
      const uploadedUrls = responses.map((response) => response.data.url);
      const nextImages = [...formData.images, ...uploadedUrls].slice(0, 4);
      setFormData((current) => ({ ...current, images: nextImages, image: nextImages[0] || "" }));
      setImagePreviews(nextImages.map(getImageUrl));
      toast.success(`${uploadedUrls.length} image${uploadedUrls.length > 1 ? "s" : ""} uploaded successfully`);
    } catch (error) {
      toast.error("Failed to upload images");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeProductImage = (index) => {
    const nextImages = formData.images.filter((_, imageIndex) => imageIndex !== index);
    setFormData((current) => ({ ...current, images: nextImages, image: nextImages[0] || "" }));
    setImagePreviews(nextImages.map(getImageUrl));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      image: "",
      images: [],
      price: "",
      agency_price: "",
      avg_delivery_time: "",
      in_stock: true,
      is_enabled: true,
      allow_quick_book: false,
      is_other_product: false,
      category_id: ""
    });
    setSelectedProduct(null);
    setImagePreviews([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setSelectedProduct(product);
    const images = (product.images?.length ? product.images : product.image ? [product.image] : []).slice(0, 4);
    setFormData({
      name: product.name,
      description: product.description,
      image: product.image || "",
      images,
      price: product.price.toString(),
      agency_price: product.agency_price?.toString() || "",
      avg_delivery_time: product.avg_delivery_time,
      in_stock: product.in_stock,
      is_enabled: product.is_enabled,
      allow_quick_book: product.allow_quick_book || false,
      is_other_product: product.is_other_product || false,
      category_id: product.category_id || ""
    });
    setImagePreviews(images.map(getImageUrl));
    setDialogOpen(true);
  };

  const openDeleteDialog = (product) => {
    setSelectedProduct(product);
    setDeleteDialogOpen(true);
  };

  const handleToggleEnabled = async (product) => {
    try {
      await axios.put(`${API}/products/${product.id}`, { is_enabled: !product.is_enabled }, getAuthHeaders());
      toast.success(`Product ${!product.is_enabled ? 'enabled' : 'disabled'}`);
      fetchProducts();
    } catch (error) {
      toast.error("Failed to update product");
    }
  };

  const handleToggleStock = async (product) => {
    try {
      await axios.put(`${API}/products/${product.id}`, { in_stock: !product.in_stock }, getAuthHeaders());
      toast.success(`Product marked as ${!product.in_stock ? 'In Stock' : 'Out of Stock'}`);
      fetchProducts();
    } catch (error) {
      toast.error("Failed to update product");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      ...formData,
      price: parseFloat(formData.price),
      agency_price: formData.agency_price === "" ? null : parseFloat(formData.agency_price),
      images: formData.images.slice(0, 4),
      image: formData.images[0] || null,
      category_id: formData.category_id || null
    };

    try {
      if (selectedProduct) {
        await axios.put(`${API}/products/${selectedProduct.id}`, payload, getAuthHeaders());
        toast.success("Product updated successfully");
      } else {
        await axios.post(`${API}/products`, payload, getAuthHeaders());
        toast.success("Product added successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      const message = error.response?.data?.detail || "Something went wrong";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await axios.delete(`${API}/products/${selectedProduct.id}`, getAuthHeaders());
      toast.success("Product deactivated successfully");
      setDeleteDialogOpen(false);
      setSelectedProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error("Failed to deactivate product");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    // Handle both old (/uploads/) and new (/api/uploads/) paths
    if (imagePath.startsWith("/uploads/")) {
      return `${BACKEND_URL}/api${imagePath}`;
    }
    return `${BACKEND_URL}${imagePath}`;
  };

  return (
    <div data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Package className="text-[#6C42F5]" size={28} />
            Products
          </h1>
          <p className="text-gray-600 mt-1">Manage your product catalog</p>
        </div>
        <Button
          onClick={openAddDialog}
          className="bg-[#6C42F5] text-white hover:bg-[#8C6CFF] rounded-lg glow-primary"
          data-testid="add-product-btn"
        >
          <Plus size={20} className="mr-2" />
          Add Product
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <Input
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-gray-300"
            data-testid="search-products"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-[#6C42F5]" size={32} />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <Package className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500">
              {searchTerm ? "No products found matching your search" : "No products added yet"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-bold text-gray-700 w-16">Image</TableHead>
                  <TableHead className="font-bold text-gray-700">Name</TableHead>
                  <TableHead className="font-bold text-gray-700">Category</TableHead>
                  <TableHead className="font-bold text-gray-700">Price</TableHead>
                  <TableHead className="font-bold text-gray-700">Delivery</TableHead>
                  <TableHead className="font-bold text-gray-700 text-center">Stock</TableHead>
                  <TableHead className="font-bold text-gray-700 text-center">Enabled</TableHead>
                  <TableHead className="font-bold text-gray-700 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow key={product.id} className="hover:bg-gray-50" data-testid={`product-row-${product.id}`}>
                    <TableCell>
                      {product.image ? (
                        <img src={getImageUrl(product.image)} alt={product.name} className="w-12 h-12 rounded-lg object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                          <ImageIcon className="text-gray-400" size={20} />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-gray-900">{product.name}</p>
                        <p className="text-gray-500 text-sm truncate max-w-[200px]">{product.description}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {product.category_name ? (
                        <Badge variant="secondary" className="bg-gray-100 text-gray-700">
                          {product.category_name}
                        </Badge>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <p className="font-medium text-gray-900">₹{product.price.toFixed(2)}</p>
                      {product.agency_price != null && (
                        <p className="mt-0.5 text-xs font-medium text-violet-700">
                          Agency: ₹{Number(product.agency_price).toFixed(2)}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-600">{product.avg_delivery_time}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center gap-1">
                        <Switch
                          checked={product.in_stock}
                          onCheckedChange={() => handleToggleStock(product)}
                          data-testid={`toggle-stock-${product.id}`}
                        />
                        <span className={`text-xs ${product.in_stock ? 'text-green-600' : 'text-red-500'}`}>
                          {product.in_stock ? 'In Stock' : 'Out'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={product.is_enabled}
                        onCheckedChange={() => handleToggleEnabled(product)}
                        data-testid={`toggle-enabled-${product.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(product)}
                          className="text-gray-600 hover:text-[#6C42F5]"
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(product)}
                          className="text-gray-600 hover:text-red-500"
                          data-testid={`delete-product-${product.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">
              {selectedProduct ? "Edit Product" : "Add New Product"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name" className="text-gray-700 font-medium">Name</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Product name"
                className="mt-1 bg-white border-gray-300"
                data-testid="input-product-name"
              />
            </div>
            <div>
              <Label htmlFor="description" className="text-gray-700 font-medium">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                placeholder="Product description"
                className="mt-1 bg-white border-gray-300 min-h-[80px]"
                data-testid="input-product-description"
              />
            </div>
            <div>
              <Label htmlFor="category_id" className="text-gray-700 font-medium">Category</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) => setFormData({ ...formData, category_id: value })}
              >
                <SelectTrigger className="mt-1 bg-white border-gray-300" data-testid="input-product-category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.filter(c => c.is_enabled).map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Images (up to 4)</Label>
              <div className="mt-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFileChange}
                  className="hidden"
                  id="product-image-upload"
                  data-testid="input-product-image"
                />
                <div className="grid grid-cols-4 gap-3 mb-3">
                  {imagePreviews.map((preview, index) => (
                    <div key={`${preview}-${index}`} className="relative">
                      <img src={preview} alt={`Product preview ${index + 1}`} className="w-full aspect-square rounded-lg object-cover" />
                      <button
                        type="button"
                        onClick={() => removeProductImage(index)}
                        className="absolute -right-2 -top-2 rounded-full bg-red-500 p-1 text-white shadow"
                        aria-label={`Remove image ${index + 1}`}
                      >
                        <X size={13} />
                      </button>
                      {index === 0 && (
                        <span className="absolute bottom-1 left-1 rounded bg-black/65 px-1.5 py-0.5 text-[10px] text-white">Primary</span>
                      )}
                    </div>
                  ))}
                  {imagePreviews.length === 0 && (
                    <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center">
                      <ImageIcon className="text-gray-400" size={24} />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || formData.images.length >= 4}
                    className="border-gray-300"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload size={16} className="mr-2" />
                        Add Images
                      </>
                    )}
                  </Button>
                  <span className="text-xs text-gray-500">{formData.images.length}/4 uploaded</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price" className="text-gray-700 font-medium">Price (₹)</Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  placeholder="0.00"
                  className="mt-1 bg-white border-gray-300"
                  data-testid="input-product-price"
                />
              </div>
              <div>
                <Label htmlFor="agency_price" className="text-gray-700 font-medium">Agency Price (₹)</Label>
                <Input
                  id="agency_price"
                  name="agency_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.agency_price}
                  onChange={handleChange}
                  placeholder="Optional"
                  className="mt-1 bg-white border-gray-300"
                  data-testid="input-product-agency-price"
                />
              </div>
              <div>
                <Label htmlFor="avg_delivery_time" className="text-gray-700 font-medium">Avg. Delivery Time</Label>
                <Input
                  id="avg_delivery_time"
                  name="avg_delivery_time"
                  value={formData.avg_delivery_time}
                  onChange={handleChange}
                  required
                  placeholder="30 mins"
                  className="mt-1 bg-white border-gray-300"
                  data-testid="input-product-delivery"
                />
              </div>
            </div>
            <div className="flex items-center justify-between py-2">
              <Label htmlFor="in_stock" className="text-gray-700 font-medium">In Stock</Label>
              <Switch
                id="in_stock"
                checked={formData.in_stock}
                onCheckedChange={(checked) => setFormData({ ...formData, in_stock: checked })}
                data-testid="input-product-stock"
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <Label htmlFor="is_enabled" className="text-gray-700 font-medium">Enabled</Label>
              <Switch
                id="is_enabled"
                checked={formData.is_enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, is_enabled: checked })}
                data-testid="input-product-enabled"
              />
            </div>
            <div className="flex items-center gap-3 py-2">
              <input
                type="checkbox"
                id="allow_quick_book"
                checked={formData.allow_quick_book}
                onChange={(e) => setFormData({ ...formData, allow_quick_book: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-[#6C42F5] accent-[#6C42F5] cursor-pointer"
                data-testid="input-product-quick-book"
              />
              <Label htmlFor="allow_quick_book" className="text-gray-700 font-medium cursor-pointer">
                Allow Quick Book
              </Label>
            </div>
            <div className="flex items-center gap-3 py-2">
              <input
                type="checkbox"
                id="is_other_product"
                checked={formData.is_other_product}
                onChange={(e) => setFormData({ ...formData, is_other_product: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-[#6C42F5] accent-[#6C42F5] cursor-pointer"
                data-testid="input-product-other"
              />
              <Label htmlFor="is_other_product" className="text-gray-700 font-medium cursor-pointer">
                Other Products
              </Label>
            </div>
            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-gray-300 text-gray-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting || uploading}
                className="bg-[#6C42F5] text-white hover:bg-[#8C6CFF]"
                data-testid="submit-product-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {selectedProduct ? "Updating..." : "Adding..."}
                  </>
                ) : (
                  selectedProduct ? "Update Product" : "Add Product"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">Deactivate Product</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 mt-2">
            Are you sure you want to deactivate <span className="font-semibold">{selectedProduct?.name}</span>?
            It will no longer be available for ordering but will remain in past order history.
          </p>
          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="border-gray-300 text-gray-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={submitting}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deactivating...
                </>
              ) : (
                "Deactivate"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
